import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { TranslateModule } from '@ngx-translate/core';
import { ThemeService } from 'core/services/theme.service';
import { FilterFieldConfig } from 'shared/components/organisms/data-table-filter/filter-field-config';
import { ButtonSize, ButtonStyle, ButtonType } from 'shared/enums';
import { ButtonComponent } from '../../atoms';

@Component({
  selector: 'app-data-table-filter',
  templateUrl: './data-table-filter.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatExpansionModule, ReactiveFormsModule, ButtonComponent, TranslateModule],
})
export class DataTableFilterComponent implements OnChanges {
  @Input() public fieldConfigs: FilterFieldConfig[] = [];
  @Input() public filtersForm: FormGroup | undefined;
  @Input() public headerTemplate: TemplateRef<unknown> | null = null;
  @Input() public inlineFooter = false;
  @Input() public showAdditionalFilters = true;
  @Input() public excludeFromCount: string[] = [];
  @Output() public applyFilters = new EventEmitter<never>();
  @Output() public clearFilters = new EventEmitter<void>();
  public filterCount = 0;
  protected readonly ButtonStyle = ButtonStyle;
  protected readonly ButtonType = ButtonType;
  protected readonly ButtonSize = ButtonSize;
  private destroyRef = inject(DestroyRef);
  private themeService = inject(ThemeService);

  private _panel!: MatExpansionPanel;

  @ViewChild(MatExpansionPanel)
  protected set panel(panel: MatExpansionPanel) {
    this._panel = panel;
  }

  private _container!: ViewContainerRef;

  @ViewChild('container', { read: ViewContainerRef })
  private set container(container: ViewContainerRef) {
    this._container = container;
    if (container && this.fieldConfigs && this.filtersForm) {
      this.renderComponents();
    }
  }

  public resetForm(): void {
    this.clearFilters.emit();
    this.filterCount = this.getFilterCount();
  }

  public submit(): void {
    const formValue = this.filtersForm?.value;
    this.applyFilters.emit(formValue);
    if (!this.themeService.isDesktop()) {
      this._panel.close();
    }
    this.filterCount = this.getFilterCount();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['fieldConfigs'] && this._container) {
      this._container.clear();
      this.renderComponents();
    }
    if (changes['showAdditionalFilters']) {
      this._panel?.close();
      //preventing expression changed after it's been checked.
      this.filterCount = this.getFilterCount();
    }
  }

  private renderComponents(): void {
    this.fieldConfigs.forEach(({ component, controlName, componentInputs, disabled }) => {
      const formControl = this.filtersForm?.get(controlName);
      const componentRef = this._container.createComponent<ControlValueAccessor>(component);
      if (!formControl) {
        console.warn(`FormControl ${controlName} not found in filtersForm`);
        return;
      }
      const instance = componentRef.instance;

      if (componentInputs) {
        Object.entries(componentInputs).forEach(([key, value]) => {
          componentRef.setInput(key, value);
        });
      }

      if (disabled) {
        if (instance.setDisabledState) {
          instance.setDisabledState(true);
        } else {
          console.error(`Component ${component.name} does not implement setDisabledState`);
        }
      } else {
        // Initialize with the form control's current value
        instance.writeValue(formControl.value);

        // Listen to control changes and update the component
        formControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
          instance.writeValue(value);
        });

        // Register onChange to sync component updates back to the form control
        instance.registerOnChange((value: unknown) => {
          formControl.setValue(value, { emitEvent: false }); // Avoids infinite loop
          this.filterCount = this.getFilterCount();
        });

        // Register onTouched to mark control as touched
        instance.registerOnTouched(() => {
          formControl.markAsTouched();
        });
      }
    });
  }

  private getFilterCount(): number {
    let filterCount = 0;

    const isFilterExcluded = (key: string): boolean => this.excludeFromCount.includes(key);
    const hasArrayValues = (value: unknown): boolean => Array.isArray(value) && value.length > 0;
    const hasObjectValues = (value: unknown): boolean => {
      const dateValue = value as {
        dateFrom: string | null;
        dateTo: string | null;
      };
      return typeof value === 'object' && value !== null && !!dateValue.dateFrom && !!dateValue.dateTo;
    };

    const hasValue = (value: unknown): boolean => value !== null && value !== '' && value !== undefined;

    const isValueValid = (value: unknown): boolean => {
      if (Array.isArray(value)) {
        return hasArrayValues(value);
      }
      if (typeof value === 'object') {
        return hasObjectValues(value);
      }
      return hasValue(value);
    };

    const formValue = this.filtersForm?.value;

    Object.entries(formValue as Record<string, unknown>).forEach(([key, value]) => {
      if (!isFilterExcluded(key) && isValueValid(value)) {
        filterCount++;
      }
    });

    return filterCount;
  }
}
